import { useRef, useState } from 'react';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { Box, CircularProgress, Stack, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import prettyBytes from 'pretty-bytes';

import { fetchROC } from '@/adapters/fetchExaCare';
import { useSnackbar } from '@/hooks/useSnackbar';

export interface RocDocumentDownloadArgs {
  referralId: string;
  documentId: string;
  fileName: string;
}

interface DocumentMessageCardProps {
  referralId: string;
  documentId: string;
  fileName: string;
  fileSizeBytes: number;
  isSender: boolean;
  onOpenDocument?: (args: RocDocumentDownloadArgs) => Promise<void>;
}

export const DocumentMessageCard: React.FC<DocumentMessageCardProps> = ({
  referralId,
  documentId,
  fileName,
  fileSizeBytes,
  isSender,
  onOpenDocument
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const isOpeningRef = useRef(false);
  const { showSnackbar } = useSnackbar();

  const handleOpen = async () => {
    if (isOpeningRef.current) {
      return;
    }
    isOpeningRef.current = true;
    setIsLoading(true);
    try {
      if (onOpenDocument) {
        await onOpenDocument({ referralId, documentId, fileName });
      } else {
        const { url } = await fetchROC.get<{ url: string }>(
          `/referrals/${referralId}/documents/${documentId}/download-url`
        );
        const parsed = new URL(url);
        if (parsed.protocol !== 'https:' && parsed.protocol !== 'http:') {
          throw new Error('Unexpected download URL protocol');
        }
        const a = document.createElement('a');
        a.href = parsed.toString();
        a.target = '_blank';
        a.rel = 'noopener noreferrer';
        document.body.appendChild(a);
        a.click();
        a.remove();
      }
    } catch {
      showSnackbar({
        message: 'Failed to open document. Please try again.',
        severity: 'error'
      });
    } finally {
      isOpeningRef.current = false;
      setIsLoading(false);
    }
  };

  return (
    <CardContainer isSender={isSender} onClick={handleOpen}>
      <Stack direction="row" gap={1.5} alignItems="center">
        <InsertDriveFileIcon
          sx={{
            fontSize: 28,
            color: isSender ? 'common.white' : 'primary.main',
            flexShrink: 0
          }}
        />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            sx={{
              fontSize: 13,
              fontWeight: 500,
              color: isSender ? 'common.white' : 'text.primary',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}
          >
            {fileName}
          </Typography>
          {fileSizeBytes > 0 && (
            <Typography
              sx={{
                fontSize: 11,
                color: isSender ? 'rgba(255,255,255,0.7)' : 'text.secondary'
              }}
            >
              {prettyBytes(fileSizeBytes)}
            </Typography>
          )}
        </Box>
        <Box
          sx={{
            flexShrink: 0,
            color: isSender ? 'common.white' : 'primary.main',
            display: 'flex'
          }}
        >
          {isLoading ? (
            <CircularProgress size={18} color="inherit" />
          ) : (
            <OpenInNewIcon sx={{ fontSize: 18 }} />
          )}
        </Box>
      </Stack>
    </CardContainer>
  );
};

const CardContainer = styled(Stack, {
  shouldForwardProp: (prop) => prop !== 'isSender'
})<{ isSender: boolean }>(({ theme, isSender }) => ({
  background: isSender ? theme.palette.primary.dark : theme.palette.info.main,
  borderRadius: 8,
  maxWidth: 360,
  padding: '12px 16px',
  cursor: 'pointer',
  '&:hover': {
    opacity: 0.9
  }
}));
